import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Text from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import {
  type CompanyInput,
  companyInputSchema,
  type PersonInput,
  personInputSchema,
} from "@gtm/contracts/core/entity-inputs";
import { PlusIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ZodError } from "zod";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { DrawerStack } from "@/components/workspace/drawer-content";
import { SelectField } from "@/components/workspace/select-field";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";

export function CreateEntityButton({ kind }: { kind: "person" | "company" }) {
  const { update } = useUrlState();
  return (
    <Button
      label={`New ${kind}`}
      icon={PlusIcon}
      onClick={() =>
        update({
          personId: null,
          companyId: null,
          personEdit: null,
          companyEdit: null,
          personTab: null,
          companyTab: null,
          companyPage: null,
          activityId: null,
          runId: null,
          [`${kind}Id`]: "new",
        })
      }
    />
  );
}
export function EntityEditors() {
  const { params } = useUrlState();
  const person = params.get("personId"),
    company = params.get("companyId");
  const kind =
    person && (person === "new" || params.get("personEdit") === "1")
      ? "person"
      : company && (company === "new" || params.get("companyEdit") === "1")
        ? "company"
        : null;
  const retained = useRetainedValue<{ kind: "person" | "company"; id: string }>(
    kind ? { kind, id: params.get(`${kind}Id`)! } : null,
  );
  return retained ? (
    <EntityEditor
      key={`${retained.kind}:${retained.id}`}
      open={!!kind}
      kind={retained.kind}
      id={retained.id}
    />
  ) : null;
}
const emptyPerson: PersonInput = {
  name: "",
  firstName: null,
  lastName: null,
  companyId: null,
  location: null,
  publicEmail: null,
  phoneNumber: null,
  website: null,
  bio: null,
};
const emptyCompany: CompanyInput = { name: "", domain: null, url: null };
function EntityEditor({
  kind,
  id,
  open,
}: {
  open: boolean;
  kind: "person" | "company";
  id: string;
}) {
  const { update } = useUrlState();
  const isNew = id === "new";
  const data = useQuery({
    queryKey: ["entity-editor", kind, id],
    enabled: open && !isNew,
    queryFn: async ({ signal }): Promise<PersonInput | CompanyInput> => {
      if (kind === "person") {
        const p = await unwrap(
          apiClient().GET("/people/{id}", { params: { path: { id } }, signal }),
        );
        return {
          name: p.name,
          firstName: p.firstName,
          lastName: p.lastName,
          companyId: p.companyId,
          location: p.location,
          publicEmail: p.publicEmail,
          phoneNumber: p.phoneNumber,
          website: p.website,
          bio: p.bio,
        };
      }
      const c = await unwrap(
        apiClient().GET("/companies/{id}", {
          params: { path: { id } },
          signal,
        }),
      );
      return { name: c.name, domain: c.domain, url: c.url };
    },
  });
  const close = () => update({ [`${kind}Id`]: isNew ? null : id, [`${kind}Edit`]: null });
  if (!isNew && !data.data)
    return (
      <SurfaceDialog open={open} title={`Edit ${kind}`} onClose={close}>
        <QueryState error={data.error} onRetry={() => void data.refetch()} />
      </SurfaceDialog>
    );
  return (
    <EntityForm
      open={open}
      kind={kind}
      id={isNew ? undefined : id}
      initial={isNew ? (kind === "person" ? emptyPerson : emptyCompany) : data.data!}
      onClose={close}
    />
  );
}
function EntityForm({
  open,
  kind,
  id,
  initial,
  onClose,
}: {
  open: boolean;
  kind: "person" | "company";
  id?: string;
  initial: PersonInput | CompanyInput;
  onClose: () => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const [value, setValue] = useState(initial),
    [busy, setBusy] = useState(false);
  const { update } = useUrlState(),
    client = useQueryClient(),
    { toast } = useToast();
  const companies = useQuery({
    queryKey: ["company-options"],
    enabled: open && kind === "person",
    queryFn: () => unwrap(apiClient().GET("/companies/options")),
  });
  useDialogReset(open, () => {
    setFormError(undefined);
    setValue(initial);
    setBusy(false);
  });
  const dirty = JSON.stringify(value) !== JSON.stringify(initial);
  function field(key: string, label: string) {
    const v = (value as unknown as Record<string, string | null>)[key];
    return (
      <TextInput
        key={key}
        label={label}
        ref={(input) => input?.setAttribute("aria-label", label)}
        value={v || ""}
        onChange={(next) => setValue({ ...value, [key]: next || (key === "name" ? "" : null) })}
        fillWidth
        variant={InputVariant.BASE}
        isDisabled={busy}
      />
    );
  }
  async function save() {
    setFormError(undefined);
    setBusy(true);
    try {
      let saved: { id: string };
      if (kind === "person") {
        const input = personInputSchema.parse(value);
        const changes = Object.fromEntries(
          Object.entries(input).filter(
            ([key, v]) => v !== (initial as unknown as Record<string, unknown>)[key],
          ),
        );
        saved = await unwrap(
          id
            ? apiClient().PATCH("/people/{id}", {
                params: { path: { id } },
                body: changes,
              })
            : apiClient().POST("/people", { body: input }),
        );
      } else {
        const input = companyInputSchema.parse(value);
        saved = await unwrap(
          id
            ? apiClient().PATCH("/companies/{id}", {
                params: { path: { id } },
                body: input,
              })
            : apiClient().POST("/companies", { body: input }),
        );
      }
      await client.invalidateQueries();
      window.dispatchEvent(new Event("records:changed"));
      update({ [`${kind}Id`]: saved.id, [`${kind}Edit`]: null });
      toast({
        variant: ToastVariant.SUCCESS,
        header: `${kind === "person" ? "Person" : "Company"} ${id ? "updated" : "created"}`,
        description: value.name.trim(),
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } catch (error) {
      setFormError(
        error instanceof ZodError
          ? error.issues[0]?.message
          : error instanceof Error
            ? error.message
            : "Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open={open}
      flush
      title={`${id ? "Edit" : "New"} ${kind}`}
      onClose={onClose}
      dirty={dirty}
      busy={busy}
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            onClick={onClose}
            isDisabled={busy}
          />
          <Button
            label={id ? "Save changes" : `Create ${kind}`}
            onClick={() => void save()}
            isLoading={busy}
            isDisabled={busy || !value.name.trim() || (!!id && !dirty)}
          />
        </>
      }
    >
      {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
      <DrawerStack>
        {field("name", "Name")}
        {kind === "company" ? (
          <>
            {field("domain", "Domain")}
            {field("url", "Website")}
          </>
        ) : (
          <>
            {field("firstName", "First name")}
            {field("lastName", "Last name")}
            <SelectField
              label="Company"
              search
              value={(value as PersonInput).companyId || ""}
              onChange={(companyId) => setValue({ ...value, companyId: companyId || null })}
              disabled={busy || companies.isPending}
              options={[
                { value: "", label: "No company" },
                ...(companies.data || []).map((c) => ({
                  value: c.id,
                  label: c.name,
                })),
              ]}
            />
            {companies.error && <Text>Could not load companies.</Text>}
            {field("location", "Location")}
            {field("publicEmail", "Email")}
            {field("phoneNumber", "Phone number")}
            {field("website", "Website")}
            {field("bio", "Bio")}
          </>
        )}
      </DrawerStack>
    </SurfaceDialog>
  );
}
